import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Input } from './input';
import { useDebounce } from '../../../hooks/useDebounce';

export interface SearchFilterProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /**
   * Valor inicial opcional
   */
  initialValue?: string;
  /**
   * Callback ejecutado al aplicar el debounce de búsqueda
   */
  onSearch: (query: string) => void;
  /**
   * Retraso del debounce en milisegundos. Default: 300ms
   */
  debounceDelay?: number;
}

/**
 * SearchFilter
 * Componente con responsabilidad única de capturar búsquedas de texto.
 * Utiliza un debounce interno para no saturar al momento de escribir.
 * 
 * @example
 * ```tsx
 * const [query, setQuery] = useState('');
 * 
 * return (
 *   <SearchFilter 
 *     placeholder="Buscar por nombre..." 
 *     onSearch={(value) => setQuery(value)} 
 *   />
 * );
 * ```
 */
export function SearchFilter({ 
  initialValue = '', 
  onSearch, 
  debounceDelay = 300,
  className = '',
  placeholder = 'Buscar...',
  ...props 
}: SearchFilterProps) {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebounce(value, debounceDelay);

  // Effect to trigger search when debounced value changes
  useEffect(() => {
    onSearch(debouncedValue);
  }, [debouncedValue, onSearch]);

  // Update internal value if external initialValue changes
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="pl-10"
        {...props}
      />
    </div>
  );
}
